import React from 'react';
import {
    Calendar,
    Clock,
    Palmtree,
    PartyPopper,
    ArrowRight,
    Info
} from 'lucide-react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

dayjs.locale('tr');

const MyLeaveSummaryWidget = ({ stats, loading, onNavigate }) => {
    const goToLeaveManagement = () => {
        if (onNavigate) {
            onNavigate('leave_management');
        } else {
            localStorage.setItem('currentPage', 'leave_management');
            window.location.reload();
        }
    };

    if (loading) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 h-full flex items-center justify-center p-6">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
            </div>
        );
    }

    const hasAnyData =
        (stats?.currentUserAnnualLeaveBalanceDays !== null && stats?.currentUserAnnualLeaveBalanceDays !== undefined) ||
        stats?.currentUserUpcomingLeavePreview != null ||
        stats?.currentUserPendingLeaveRequestCount > 0 ||
        stats?.currentUserUpcomingHolidayName != null;

    const {
        currentUserAnnualLeaveBalanceDays,
        currentUserUpcomingLeavePreview,
        currentUserPendingLeaveRequestCount,
        currentUserUpcomingHolidayName,
        currentUserUpcomingHolidayDate
    } = stats || {};

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden relative group transition-all hover:shadow-2xl h-full flex flex-col">
            <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 rounded-full bg-emerald-500/10 blur-2xl pointer-events-none"></div>

            {/* Header */}
            <div className="p-3 pb-2 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/10 shrink-0">
                <div className="flex items-center space-x-2">
                    <div className="p-1.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-md shadow-inner text-white">
                        <Palmtree size={16} />
                    </div>
                    <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">İzin Özeti</h3>
                </div>
                <button
                    type="button"
                    onClick={goToLeaveManagement}
                    className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 flex items-center gap-1 px-2 py-1 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors"
                >
                    <span>İzinlerim</span>
                    <ArrowRight size={12} />
                </button>
            </div>

            {/* Body */}
            {!hasAnyData ? (
                <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                    <Calendar size={40} className="mb-3 text-gray-300 dark:text-gray-600" strokeWidth={1.5} />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Şu an gösterilecek bir izin bilginiz bulunmuyor.</p>
                    <button
                        type="button"
                        onClick={goToLeaveManagement}
                        className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
                    >
                        İzinlerim Sayfasına Git
                    </button>
                </div>
            ) : (
                <div className="flex-1 p-3.5 flex flex-col gap-2.5 overflow-y-auto">
                    {/* Yıllık İzin Bakiyesi */}
                    {currentUserAnnualLeaveBalanceDays !== null && currentUserAnnualLeaveBalanceDays !== undefined && (
                        <div className="flex items-center justify-between p-3 bg-gray-50/80 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700 rounded-xl hover:border-blue-200 dark:hover:border-blue-800/60 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-blue-100/80 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
                                    <Calendar size={18} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[11px] text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide">
                                        Kalan Yıllık İzin
                                    </span>
                                    <span className="text-gray-800 dark:text-gray-100 font-bold text-sm">
                                        <span className="text-base text-blue-600 dark:text-blue-400 mr-1">{currentUserAnnualLeaveBalanceDays}</span>
                                        Gün
                                    </span>
                                </div>
                            </div>
                            <span title="Toplam yıllık izin bakiyeniz (Devreden + Hakedilen - Kullanılan)">
                                <Info size={15} className="text-gray-400 dark:text-gray-500 cursor-help" />
                            </span>
                        </div>
                    )}

                    {/* Yaklaşan İzin */}
                    {currentUserUpcomingLeavePreview && (
                        <div className="flex items-center justify-between p-3 bg-emerald-50/60 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/50 rounded-xl transition-colors">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                                    <Palmtree size={18} />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-[11px] text-emerald-700 dark:text-emerald-400 font-semibold uppercase tracking-wide">
                                        Yaklaşan İzniniz
                                    </span>
                                    <span className="text-emerald-950 dark:text-emerald-200 font-semibold text-xs truncate">
                                        {currentUserUpcomingLeavePreview.displayText}
                                    </span>
                                </div>
                            </div>
                            <span className="text-emerald-700 dark:text-emerald-300 font-bold px-2 py-0.5 bg-emerald-100/80 dark:bg-emerald-900/50 rounded-md text-xs shrink-0 ml-2">
                                {currentUserUpcomingLeavePreview.totalDays} Gün
                            </span>
                        </div>
                    )}

                    {/* Onay Bekleyen İzinler */}
                    {currentUserPendingLeaveRequestCount > 0 && (
                        <div
                            onClick={goToLeaveManagement}
                            className="flex items-center justify-between p-3 bg-amber-50/60 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/50 rounded-xl hover:border-amber-300 dark:hover:border-amber-700 transition-colors cursor-pointer"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center text-amber-600 dark:text-amber-400 shrink-0">
                                    <Clock size={18} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[11px] text-amber-700 dark:text-amber-400 font-semibold uppercase tracking-wide">
                                        Onay Bekleyen
                                    </span>
                                    <span className="text-amber-950 dark:text-amber-200 font-semibold text-xs">
                                        <strong className="text-sm mr-1">{currentUserPendingLeaveRequestCount}</strong>
                                        İzin Talebiniz Var
                                    </span>
                                </div>
                            </div>
                            <ArrowRight size={15} className="text-amber-500 dark:text-amber-400" />
                        </div>
                    )}

                    {/* Yaklaşan Resmi Tatil */}
                    {currentUserUpcomingHolidayName && currentUserUpcomingHolidayDate && (
                        <div className="flex items-center justify-between p-3 bg-purple-50/60 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800/50 rounded-xl transition-colors">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center text-purple-600 dark:text-purple-400 shrink-0">
                                    <PartyPopper size={18} />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-[11px] text-purple-700 dark:text-purple-400 font-semibold uppercase tracking-wide">
                                        Yaklaşan Resmi Tatil
                                    </span>
                                    <span className="text-purple-950 dark:text-purple-200 font-semibold text-xs truncate">
                                        {dayjs(currentUserUpcomingHolidayDate).format('DD MMMM')} — {currentUserUpcomingHolidayName}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MyLeaveSummaryWidget;
